import { useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useState } from 'react'
import api, { type IOrdinalOrder } from 'api/api'
import useBsvDisplay from 'components/shared/useBsvDisplay'
import { currentUserHasOrdinalsMarket } from 'components/panels/ordinalMarketFeature'
import {
    isListedOrder,
    myOrdinalOrdersPollInterval,
    ordinalOrderRow,
} from './myOrdinalOrdersPolicy'
import styles from './my-ordinal-orders.module.scss'

function shortOrigin(origin?: string | null): string {
    if (!origin) return 'an ordinal'
    return `${origin.slice(0, 6)}…${origin.slice(-4)}`
}

// Your open listings and offers, each with its Cancel or Withdraw. A signed offer
// stays here while its funds are committed, even after it closed in the app.
// Renders nothing when there is nothing open.
export default function MyOrdinalOrders({
    enabled = true,
    className,
}: {
    enabled?: boolean
    className?: string
}) {
    const queryClient = useQueryClient()
    const userId = gon.currentUser?.id
    const queryKey = ['myOrdinalOrders', userId]
    const { formatSatsAmount } = useBsvDisplay()
    const [busyOrderId, setBusyOrderId] = useState<string | null>(null)
    const [errors, setErrors] = useState<Record<string, string>>({})

    const { data } = useQuery({
        queryKey,
        queryFn: () => api.getMyOrdinalOrders(),
        enabled: enabled && !!userId && currentUserHasOrdinalsMarket(),
        staleTime: 15 * 1000,
        retry: false,
        refetchInterval: query => myOrdinalOrdersPollInterval(query.state.data?.orders),
    })

    const orders = (data?.orders || []).filter(isListedOrder)
    if (!orders.length) return null

    const act = async (order: IOrdinalOrder, action: 'cancel' | 'withdraw') => {
        const ordinalId = String(order.ordinal?.id || order.ordinal_id)
        setBusyOrderId(order.id)
        setErrors(current => ({ ...current, [order.id]: '' }))
        try {
            if (action === 'withdraw') {
                await api.withdrawOrdinalOrder(ordinalId, order.id)
            } else {
                await api.cancelOrdinalOrder(ordinalId, order.id)
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Something went wrong.'
            setErrors(current => ({ ...current, [order.id]: message }))
        } finally {
            setBusyOrderId(null)
            queryClient.invalidateQueries({ queryKey })
        }
    }

    const rootClassName = className ? `${styles.orders} ${className}` : styles.orders
    return (
        <section className={rootClassName} data-testid="my-ordinal-orders">
            <div className={styles.heading}>Your listings and offers</div>
            <ul className={styles.list}>
                {orders.map(order => {
                    const row = ordinalOrderRow(order)
                    const ordinalId = order.ordinal?.id || order.ordinal_id
                    const error = errors[order.id]
                    return (
                        <li key={order.id} className={styles.row}>
                            <img
                                className={styles.thumb}
                                src={`${window.knovApiUrl}/api/v1/bsv/ordinals/${ordinalId}/preview_image?size=small`}
                                alt=""
                                loading="lazy"
                            />
                            <div className={styles.body}>
                                <div className={styles.title}>
                                    {order.side === 'bid' ? 'Offer' : 'Listing'} on{' '}
                                    {shortOrigin(order.ordinal?.origin)}
                                </div>
                                <div>
                                    {formatSatsAmount(order.price_sats)} · {row.label}
                                </div>
                                {error ? (
                                    <div className={styles.error} role="alert">
                                        {error}
                                    </div>
                                ) : (
                                    row.detail && <div>{row.detail}</div>
                                )}
                            </div>
                            {row.action && (
                                <button
                                    type="button"
                                    className={styles.action}
                                    disabled={busyOrderId === order.id}
                                    onClick={() => act(order, row.action!)}
                                >
                                    {row.action === 'withdraw' ? 'Withdraw' : 'Cancel'}
                                </button>
                            )}
                        </li>
                    )
                })}
            </ul>
        </section>
    )
}
